import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputLabel,
  TextField,
} from '@mui/material';
import { memo, useEffect, useMemo, useState } from 'react';
import { CloseIcon } from '~/assets/icons';
import { useFormik } from 'formik';
import { DIALOG_MODE_CREATE, DIALOG_MODE_EDIT } from '~/utils/constants/common';
import { cast, handleApiError, notify } from '~/utils/helper';
import { BaseDatePicker, BaseRow, BaseSelect, SelectAssigneeInput } from '~/components/common';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '~/redux/hooks';
import dayjs from 'dayjs';
import { handleCreateTaskCustomer, handleUpdateTaskCustomer } from '~/thunks/task/taskThunk';
import MenuOption from '~/components/jobs/menu-option-file/MenuOption';
import { IEditTask } from '~/utils/interface/task';
import { taskActions } from '~/thunks/task/taskSlice';
import { ITask } from '~/utils/interface/task';
import { ICustomer } from '~/utils/interface/customer';
import FileItem from '~/components/estimate/file-item/FileItem';
import { FILE_ITEM_FOR_CUSTOMER } from '~/utils/constants/jobs';

type EditTaskDialogProps = {
  open: boolean;
  setOpen?: (open: boolean, needRefreshData?: boolean) => void;
  mode?: string;
  data?: ITask | IEditTask | null;
  customer?: ICustomer;
};

const initialValues: IEditTask = {
  name: '',
  description: '',
  dueDate: '',
  assigneeIds: [],
  files: undefined,
  customerFileIds: [],
};

const EditTaskDialog = (props: EditTaskDialogProps) => {
  const { mode, data, customer } = props;
  const dispatch = useAppDispatch();
  const { t } = useTranslation(['admin/customer', 'admin/common', 'admin/component']);

  const { selectAccounts } = useAppSelector((state) => state.account);

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const statusOptions = useMemo(
    () => [
      {
        id: '1',
        name: t('selectStatus.statusTask.new', { ns: 'admin/component' }),
      },
      {
        id: '2',
        name: t('selectStatus.statusTask.progress', { ns: 'admin/component' }),
      },
      {
        id: '3',
        name: t('selectStatus.statusTask.completed', { ns: 'admin/component' }),
      },
    ],
    [t]
  );

  useEffect(() => {
    if (mode === DIALOG_MODE_EDIT) {
      form.setFieldValue('assigneeIds', cast(data?.assigneeIds?.[0]));
      form.setFieldValue('name', cast(data?.name));
      form.setFieldValue('dueDate', cast(data?.dueDate));
      form.setFieldValue('statusId', cast(data?.statusId));
      form.setFieldValue('description', cast(data?.description));
      form.setFieldValue('customerFileIds', cast(data?.customerFileIds));
    }
    if (mode === DIALOG_MODE_CREATE) {
      form.setFieldValue('customerId', customer?.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, data, customer]);

  const handleCancel = () => {
    handleSetOpen(false);
    form.resetForm();
  };
  const form = useFormik({
    validateOnChange: false,
    initialValues,
    onSubmit: async (values, { setFieldError }) => {
      const updatedValues: IEditTask = {
        ...values,
        assigneeIds: Array.isArray(values.assigneeIds)
          ? values.assigneeIds.filter((id) => id !== undefined && id !== null)
          : values.assigneeIds
          ? [values.assigneeIds]
          : [],
      };

      if (mode === DIALOG_MODE_CREATE) {
        try {
          delete updatedValues.status;
          await dispatch(handleCreateTaskCustomer({ data: updatedValues })).unwrap();
          notify('success', t('createSuccess', { ns: 'admin/common' }));
          await dispatch(taskActions.changeNeedRefreshData(true));
          handleSetOpen(false);
        } catch (e: unknown) {
          handleApiError(e, setFieldError);
        }
        return;
      }

      if (mode === DIALOG_MODE_EDIT) {
        try {
          await dispatch(handleUpdateTaskCustomer({ id: data?.id, data: updatedValues })).unwrap();
          notify('success', t('editSuccess', { ns: 'admin/common' }));
          handleSetOpen(false);
          await dispatch(taskActions.changeNeedRefreshData(true));
        } catch (e: unknown) {
          handleApiError(e, setFieldError);
        }
        return;
      }
    },
  });
  const handleSetOpen = (open: boolean, needRefreshData?: boolean) => {
    form.resetForm();
    if (props.setOpen) {
      props.setOpen(open, needRefreshData);
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <Dialog open={props.open} onClose={() => handleSetOpen(false)} fullWidth maxWidth='md'>
      <DialogTitle>
        {mode === DIALOG_MODE_CREATE ? t('addTaskDialog.addNewTask') : t('addTaskDialog.editTask')}
        <IconButton onClick={() => handleSetOpen(false)} className='absolute right-[10px] top-[10px]'>
          <CloseIcon className='w-[10px] h-[10px]' />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Box>
          <BaseRow className='gap-[20px] mb-[20px]'>
            <Box className='w-full'>
              <InputLabel>{t('addTaskDialog.assignedStaff')}</InputLabel>
              <SelectAssigneeInput
                name='assigneeIds'
                value={form.values?.assigneeIds}
                options={selectAccounts}
                onChange={form.handleChange}
                optionInput={true}
                helperText={form.errors.assigneeIds}
              />
            </Box>

            <Box className='w-full'>
              <InputLabel>{t('addTaskDialog.taskName')}</InputLabel>
              <TextField
                name='name'
                placeholder={t('addTaskDialog.taskName')}
                value={form.values?.name || ''}
                onChange={form.handleChange}
                error={Boolean(form.errors?.name)}
                helperText={form.errors?.name}
              />
            </Box>
          </BaseRow>

          <BaseRow className='gap-[20px] mb-[20px]'>
            {mode === DIALOG_MODE_EDIT ? (
              <Box className='w-full '>
                <InputLabel>{t('addTaskDialog.status')}</InputLabel>
                <BaseSelect
                  options={statusOptions}
                  name='statusId'
                  isHideIconCheckbox={true}
                  value={form.values.statusId}
                  onChange={(e) => {
                    form.setFieldValue('statusId', e.target.value);
                  }}
                  error={Boolean(form.errors?.statusId)}
                  helperText={form.errors?.statusId}
                />
              </Box>
            ) : (
              <></>
            )}

            <Box className='w-full'>
              <InputLabel>{t('addTaskDialog.dueDate')}</InputLabel>
              <BaseDatePicker
                placeholder={t('addTaskDialog.dueDate')}
                value={form.values.dueDate}
                onChange={(date) => {
                  if (date) {
                    const selectedDate = dayjs(date).format('YYYY-MM-DD');
                    form.setFieldValue('dueDate', selectedDate);
                  }
                }}
                error={Boolean(form.errors.dueDate) || Boolean(form.errors.dueDate)}
                helperText={form.errors.dueDate || form.errors.dueDate}
              />
            </Box>
          </BaseRow>
          <BaseRow className='gap-[20px] mb-[20px]'>
            <Box className='w-full'>
              <InputLabel>{t('addTaskDialog.note')}</InputLabel>
              <TextField
                name='description'
                placeholder={t('addTaskDialog.note')}
                value={form.values?.description || ''}
                onChange={form.handleChange}
                error={Boolean(form.errors?.description)}
                helperText={form.errors?.description}
                multiline
              />
            </Box>
          </BaseRow>

          <BaseRow className='gap-[20px] mb-[10px]'>
            <Box className='w-[50%]'>
              <InputLabel>{t('addTaskDialog.organization')}</InputLabel>
              <TextField placeholder={t('addTaskDialog.organization')} value={customer?.name} disabled />
            </Box>
          </BaseRow>
        </Box>
        <FileItem
          customerId={customer ? customer?.id : data?.customerId}
          form={form}
          typeData={FILE_ITEM_FOR_CUSTOMER}
          editData={data}
        />
        <MenuOption anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleClose} handleDeleteItem={() => {}} />
      </DialogContent>

      <DialogActions className='justify-end'>
        <Button color='secondary' variant='contained' onClick={() => handleCancel()}>
          {t('cancel', { ns: 'admin/common' })}
        </Button>
        <Button
          color='primary'
          variant='contained'
          onClick={() => {
            form.handleSubmit();
          }}
        >
          {t('save', { ns: 'admin/common' })}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default memo(EditTaskDialog);
